import axios from 'axios';
import Pusher from 'pusher-js';

import { User } from './User';
import { Board } from './Board';
import { API_URL, PUSHER_AUTH_ENDPOINT, PUSHER_CLUSTER, PUSHER_KEY } from '../../config';

export class AppState {
  _user = new User();
  _pusher = null;
  _boards = [];
  dispatch = undefined;

  constructor(
    user,
    boards,
    pusher,
    dispatch
  ) {
    const token = localStorage ? localStorage.getItem('token') : null;
    this._boards = boards || [];
    this._user = user || new User();
    this._pusher = pusher || (token ? new Pusher(`${PUSHER_KEY}`, {
      cluster: `${PUSHER_CLUSTER}`,
      forceTLS: true,
      channelAuthorization: {
        endpoint: `${PUSHER_AUTH_ENDPOINT}`,
        transport: 'ajax',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    }) : null);
    this.dispatch = dispatch;
    this.getBoardsFromApi = this.getBoardsFromApi.bind(this);
    this.getBoardFromApi = this.getBoardFromApi.bind(this);
    this.getBoardsPaginated = this.getBoardsPaginated.bind(this);
    this.updateLocalBoard = this.updateLocalBoard.bind(this);
  }

  get user() {
    return this._user;
  }

  get pusher() {
    return this._pusher;
  }

  get boards() {
    return this._boards;
  }

  set boards(boards) {
    this._boards = boards;
  }

  set user(user) {
    this._user = user;
  }

  static fromJson(json) {
    const user = User.fromJson(json.user);
    const boards = json.boards.map((board) => Board.fromJson(board));
    return new AppState(user, boards, null, json.dispatch);
  }

  toJson() {
    return {
      user: this._user.toJson(),
      boards: this._boards.map((board) => board.toJson()),
      dispatch: this.dispatch,
    };
  }

  async getBoardsFromApi() {
    const url = this._user.companyId ? `${API_URL}/boards/user/${this._user.id}/company/${this._user.companyId}` : `${API_URL}/boards/user/${this._user.id}`;
    const boards = await axios({
      url: this._user?.rol?.name === 'superadmin' ? `${API_URL}/boards/company/${this._user.companyId}` : url,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this._user.token}`,
      },
    }).then(({ data }) => {
      const boards = data.map((board) => Board.fromJson(board));
      return boards;
    });

    this._boards = boards;
    this.dispatch &&  this.dispatch({ type: 'UPDATE_STATE' });

    return boards;
  }

  async getBoardsPaginated({ page = 1, limit = 20, search = '', tab = 'all' } = {}) {
    if (!this._user.companyId || !this._user.id) {
      return { boards: [], pagination: { total: 0, hasMore: false } };
    }

    const params = new URLSearchParams({ page, limit, search, tab });
    const { data } = await axios({
      url: `${API_URL}/boards/user/${this._user.id}/company/${this._user.companyId}/paginated?${params}`,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this._user.token}`,
      },
    });

    const boards = data.boards.map((board) => Board.fromJson(board));
    return { boards, pagination: data.pagination };
  }

  boardsToJSON() {
    return this._boards.map((board) => board.toJson());
  }

  async getBoardFromApi(id) {
    const board = await axios({
      url: `${API_URL}/boards/${id}`,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this._user.token}`,
      },
    }).then(({ data }) => {
      return Board.fromJson(data);
    });

    if (!board) {
      return;
    }

    if (this._boards.find((item) => board.id === item.id)) {
      this._boards = this._boards.map((b) => (b.id === board.id ? board : b));
      this.dispatch && this.dispatch({ type: 'UPDATE_STATE' });
      return board;
    }

    this._boards.push(board);
    this.dispatch &&  this.dispatch({ type: 'UPDATE_STATE' });

    return board;
  }

  async createBoard(board) {
    const newBoard = await axios({
      url: `${API_URL}/boards`,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this._user.token}`,
      },
      data: board,
    }).then(({ data }) => {
      return Board.fromJson(data);
    });

    this._boards.push(newBoard);
    this.dispatch({ type: 'UPDATE_STATE' });
  }

  async updateBoard(board) {
    const updatedBoard = await axios({
      url: `${API_URL}/boards/${board.id}`,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this._user.token}`,
      },
      data: board,
    }).then(({ data }) => {
      return Board.fromJson(data);
    });

    const index = this._boards.findIndex((b) => b.id === updatedBoard.id);
    this._boards[index] = updatedBoard;
    this.dispatch && this.dispatch({ type: 'UPDATE_STATE' });
  }

  async deleteBoard(id) {
    await axios({
      url: `${API_URL}/boards/${id}`,
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this._user.token}`,
      },
    });

    this._boards = this._boards.filter((board) => board.id !== id);
    this.dispatch && this.dispatch({ type: 'UPDATE_STATE' });
  }

  updateLocalBoard(board) {
    if (!board) {
      return;
    }
    // `_id ?? id`: Board.fromJson acepta las dos formas; buscar solo por `_id` perdia el refresco en
    // silencio si el servidor respondiera `id`.
    const boardId = board._id ?? board.id;
    if (!this._boards.some((b) => b.id === boardId)) {
      return;
    }
    // Array NUEVO, no mutacion en sitio: el efecto de views/Board/index.jsx depende de la identidad
    // de `boards`, y sin ella no hay setBoard ni se recalcula boardsFormatted (al renombrar una
    // columna, `containers` se quedaba con el nombre viejo). Mismo patron que getBoardFromApi.
    const updated = Board.fromJson(board);
    this._boards = this._boards.map((b) => (b.id === boardId ? updated : b));
    this.dispatch && this.dispatch({ type: 'UPDATE_STATE' });
    return updated;
  }
}
